import { useState, useEffect } from "react";
import AddCSuiteUsersModal from "../components/AddCSuiteModal";
import { useCollection } from "react-firebase-hooks/firestore";
import { Loader } from "../hooks/useLoading";
import SendEmail from "../components/SendEmail";
// CHANGE: usersCollectionRef = your full-profile collection (rename if different)
import { cSuiteCollectionRef, userCollectionRef } from "../utils/firestoreHelpers";
import { mailType, userStatusTypes } from "../utils/constants";

const CsuiteUsers = () => {
  const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
  const [showAddModal, setShowAddModal] = useState(false);
  const [status, setStatus] = useState("Created");
  const [selected, setSelected] = useState(null);
  const [membership, setMembership] = useState("C-Suite");
  const [userData, setUserData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // CHANGE: search
  const [snapshot, loading, error] = useCollection(cSuiteCollectionRef, {
    snapshotListenOptions: {
      includeMetadataChanges: true,
    },
  });
  // CHANGE: listen to the full user profiles
  const [profileSnapshot, profileLoading] = useCollection(userCollectionRef);

  let { CREATED, SENT_MAIL, JOINED } = userStatusTypes;

  const getUserFromDb = async () => {
    try {
      setIsFetchingUsers(true);
      const nowMs = Date.now();
      const term = searchTerm.trim().toLowerCase();

      // CHANGE: profiles indexed by lowercased email
      const profiles = {};
      profileSnapshot?.docs?.forEach((p) => {
        const email = p.data()?.email;
        if (email) profiles[email.toLowerCase()] = p.data();
      });

      let data1 =
        snapshot?.docs
          ?.map((data) => {
            // CHANGE: merge profile into the c_suite_users doc (doc id = email)
            const cs = data.data();
            const profile = profiles[data.id.toLowerCase()];
            return {
              ...profile,
              ...cs, // deleted / added_by / membership_tier come from c_suite_users
              id: data.id,
              email: profile?.email ?? cs.email ?? data.id,
              // has profile -> joined (unless it has its own status); no profile -> created
              status: profile
                ? profile.status ?? cs.status ?? JOINED
                : cs.status ?? CREATED,
            };
          })
          .filter((user) =>
            ["undefined", undefined, false].includes(user.deleted)
          )
          .filter((user) => user?.membership_tier === membership)
          .filter((user) => {
            const expiryMs = user?.expiry_time?.toMillis?.() ?? null;
            if (status === "Code Expired") {
              return user.status === SENT_MAIL && expiryMs !== null && expiryMs < nowMs;
            }
            if (status === SENT_MAIL) {
              return (
                user.status === SENT_MAIL &&
                (expiryMs === null || expiryMs >= nowMs)
              );
            }
            if (status === JOINED) {
              // CHANGE: strict, no more blank "undefined" status rows
              return user.status === JOINED;
            }
            return (
              user.status === CREATED &&
              !!user.first_name?.trim() &&
              !!user.last_name?.trim()
            );
          })
          // CHANGE: search across the visible columns
          .filter((user) => {
            if (!term) return true;
            return [
              user.first_name,
              user.last_name,
              `${user.first_name ?? ""} ${user.last_name ?? ""}`,
              user.email,
              user.designation,
              user.company,
              user.mobile_number,
            ].some((v) => String(v ?? "").toLowerCase().includes(term));
          }) || [];

      setUserData(data1);
    } catch (e) {
      console.log("Failed to load c-suite users", e);
      setUserData([]);
    } finally {
      setIsFetchingUsers(false);
    }
  };

  useEffect(() => {
    getUserFromDb();

    return () => {};
    // CHANGE: re-run on profiles and search too
  }, [status, membership, snapshot, profileSnapshot, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [status, membership, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(userData.length / rowsPerPage));
  const paginatedUsers = userData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const isLoading = loading || profileLoading || isFetchingUsers;
  console.log(userData);

  return (
    <>
      <header className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100">
        <h3>C-Suite Users</h3>
        <div className="d-flex gap-4">
          {/* CHANGE: search box */}
          <div className="d-flex flex-column gap-0">
            <div>Search :</div>
            <input
              type="text"
              className="form-control"
              style={{ width: "240px" }}
              placeholder="Name, email, designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="d-flex flex-column gap-0">
            <div>Status :</div>
            <select
              style={{ width: "max-content" }}
              className="form-control"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              <option value="Created">Created</option>
              <option value="Sent Email">Sent Email</option>
              <option value="Code Expired">Code Expired</option>
              <option value="Joined">Joined</option>
            </select>
          </div>
          <div className="d-flex flex-column gap-0">
            <div>Membership :</div>
            <select
              style={{ width: "max-content" }}
              className="form-control"
              value={membership}
              onChange={(e) => {
                setMembership(e.target.value);
              }}
            >
              {/* <option value="Free">Free</option>
              <option value="Gold">Gold</option> */}
              <option value="C-Suite">C-Suite</option>
            </select>
          </div>
        </div>
        {/* <button
          className="btn btn-dark"
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          Add
        </button> */}
      </header>
      <main className="position-relative">
        {isLoading && <Loader />}
        {error ? (
          <p className="text-error">
            There is some error. Please try again later
          </p>
        ) : null}
        <table className="table">
          <thead className="table-secondary">
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Designation</th>
              <th>Dial code</th>
              <th>Contact No</th>
              <th>Membership</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && userData?.length < 1 ? (
              <tr>
                <td colSpan={8} className="text-center">
                  No Data Available
                </td>
              </tr>
            ) : null}
            {paginatedUsers?.map((data) => {
              const d = { ...data, id: data.id };
              return (
                <tr key={d.id}>
                  <td>{d.first_name}</td>
                  <td>{d.last_name}</td>
                  <td>{d.email}</td>
                  <td>{d?.designation}</td>
                  {/* CHANGE: field is mobile_number_dial in the profile */}
                  <td>{d?.mobile_number_dial ?? d?.dial_code}</td>
                  <td>{d.mobile_number}</td>
                  <td>{d.membership_tier}</td>
                  <td>
                    {status === "Created" ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => setSelected(d)}
                      >
                        Send Email
                      </button>
                    ) : status === "Code Expired" ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => setSelected(d)}
                      >
                        Resend Email
                      </button>
                    ) : (
                      d.status
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!isLoading ? (
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge text-bg-secondary px-3 py-2">
                Total users: {userData.length}
              </span>
              <label htmlFor="csuite-users-per-page" className="mb-0 fw-semibold">
                Per page:
              </label>
              <select
                id="csuite-users-per-page"
                className="form-control form-control-sm"
                style={{ width: "90px" }}
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-muted small">
                Showing{" "}
                {userData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}
                -{Math.min(currentPage * rowsPerPage, userData.length)} of{" "}
                {userData.length}
              </span>
              <div className="btn-group" role="group" aria-label="Pagination">
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Previous
                </button>
                <button className="btn btn-primary btn-sm" disabled>
                  Page {currentPage} / {totalPages}
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
      {selected ? (
        <SendEmail
          data={selected}
          onClose={() => setSelected(null)}
          type={mailType.INVITATION}
        />
      ) : null}
      {showAddModal && (
        <AddCSuiteUsersModal onClose={() => setShowAddModal(false)} />
      )}
    </>
  );
};

export default CsuiteUsers;